import React from "react";

import { find } from "lodash/fp";
import { useNavigate, useParams } from "react-router";

import { Divider, Typography } from "@mui/material";

import ActionBar from "@app/analytics/components/ActionBar";
import {
  IntegrationsContent,
  IntegrationsPageContainer,
  IntegrationsSidebar,
  ScrollableContent,
  TabsContainer,
} from "@app/analytics/Integrations/styles";
import ROUTES from "@app/common/routes";
import type { IntegrationScope } from "@app/common/types";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import { Column, Row } from "@rui/Stack";
import { BackButton } from "@rui/styles";
import Tab from "@rui/Tab";
import Tabs from "@rui/Tabs";

import IntegrationDetail from "./IntegrationDetail";
import UserIntegrations from "./UserIntegrations";
import WorkspaceIntegrations from "./WorkspaceIntegrations";

const TABS = [
  {
    label: "Your Integrations",
    value: "user",
    path: ROUTES.INTEGRATIONS_GOOGLE_CALENDAR,
    content: UserIntegrations,
  },
  {
    label: "Workspace",
    value: "workspace",
    path: ROUTES.INTEGRATIONS_WORKSPACE_SLACK,
    content: WorkspaceIntegrations,
  },
];

const Integrations = () => {
  usePageTitle("Integrations");

  const navigate = useNavigate();
  const { scope } = useParams();

  const selectedTab = (scope ?? "user") as IntegrationScope;
  const selectedTabConfig = find({ value: selectedTab }, TABS) ?? TABS[0];

  const ContentComponent = selectedTabConfig.content;

  const handleChangeTab = (event: React.SyntheticEvent, tab: string) => {
    const path = find({ value: tab }, TABS)?.path;
    navigate(path!, { replace: true });
  };

  return (
    <IntegrationsPageContainer>
      <ActionBar>
        <Row variant="left" spacing={1}>
          <BackButton onClick={() => navigate(-1)} />
          <Divider orientation="vertical" sx={{ height: "20px", mr: 1 }} />
          <Typography variant="h2">Apps & Integrations</Typography>
        </Row>
      </ActionBar>
      <IntegrationsContent>
        <IntegrationsSidebar>
          <TabsContainer>
            <Tabs value={selectedTab} onChange={handleChangeTab}>
              {TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  sx={{ flexBasis: "50%" }}
                />
              ))}
            </Tabs>
          </TabsContainer>
          <ScrollableContent>
            <ContentComponent />
          </ScrollableContent>
        </IntegrationsSidebar>
        <Column flex={1}>
          <IntegrationDetail scope={selectedTab} />
        </Column>
      </IntegrationsContent>
    </IntegrationsPageContainer>
  );
};

export default Integrations;
